import React, { useState, useEffect } from "react";
import { Card, Col, Row, Alert, Form, Spinner, Button, ListGroup } from "react-bootstrap";
import { collection, getDocs, doc, getDoc, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function MyPayrollsPage() {
  const [startDate, setStartDate] = useState(new Date());
  const [payrolls, setPayrolls] = useState([]);
  const [filteredPayrolls, setFilteredPayrolls] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [rolePermissions, setRolePermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Fetch payrolls for the logged-in user
  const fetchPayrolls = async (email) => {
    try {
      const payrollQuery = await getDocs(collection(db, "payrolls"));
      const payrollData = payrollQuery.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter payrolls by user email
      const userPayrolls = payrollData.filter((payroll) => payroll.email === email);
      setPayrolls(userPayrolls);
      filterPayrollsByMonth(startDate, userPayrolls);
    } catch (error) {
      console.error("Error fetching payrolls:", error);
      setPayrolls([]);
    }
  };

  // Filter payrolls by selected month
  const filterPayrollsByMonth = (date, payrollList) => {
    const selectedMonth = date.getMonth();
    const selectedYear = date.getFullYear();

    const filteredByMonth = payrollList.filter((payroll) => {
      const payrollDate = new Date(payroll.date);
      return (
        payrollDate.getMonth() === selectedMonth &&
        payrollDate.getFullYear() === selectedYear
      );
    });

    setFilteredPayrolls(filteredByMonth);
  };

  // Fetch user documents
  const fetchDocuments = async (userId) => {
    try {
      const docsQuery = await getDocs(collection(db, "userDocuments"));
      const docsData = docsQuery.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((doc) => doc.userId === userId);
      setDocuments(docsData);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    }
  };

  // Upload selected files
  const handleUpload = async () => {
    if (!selectedFiles.length) {
      alert("Please select files to upload.");
      return;
    }
    setUploading(true);
    try {
      for (const file of selectedFiles) {
        const storageRef = ref(storage, `userDocuments/${currentUser.uid}/${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        await addDoc(collection(db, "userDocuments"), {
          userId: currentUser.uid,
          fileName: file.name,
          url: downloadURL,
          uploadedAt: new Date().toISOString(),
        });
      }
      setSelectedFiles([]);
      fetchDocuments(currentUser.uid);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Fetch user data and permissions
  const fetchUserData = async (authUser) => {
    setLoading(true);

    try {
      // Get user document
      const userDocRef = doc(db, "Users", authUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRole = userData.role || "";

        // Fetch role permissions
        if (userRole === "admin") {
          setRolePermissions({ canAccessPayrolls: true });
        } else {
          const roleDocRef = doc(db, "Roles", userRole);
          const roleDoc = await getDoc(roleDocRef);

          if (roleDoc.exists()) {
            setRolePermissions(roleDoc.data().permissions);
          } else {
            setRolePermissions(null);
          }
        }

        // Fetch payrolls for the user
        await fetchPayrolls(authUser.email);
        await fetchDocuments(authUser.uid);
      } else {
        console.warn("User document does not exist.");
        setAccessDenied(true);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  };

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      if (authUser) {
        setCurrentUser(authUser);
        fetchUserData(authUser);
      } else {
        setCurrentUser(null);
        setAccessDenied(true);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle filtering by month
  const handleDateChange = (date) => {
    setStartDate(date);
    filterPayrollsByMonth(date, payrolls);
  };

  if (accessDenied) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">You do not have permission to access this page.</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-money-dollar-circle-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">My Payroll</h1>
          </div>
          <p className="lead text-muted">View and manage your payroll information</p>
        </div>

        {/* Enhanced Filter Section */}
        <Row className="g-3 g-lg-4 mb-5">
          <Col xs={12}>
            <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
              <Card.Header className="bg-gradient-primary text-white text-center py-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center justify-content-center">
                  <i className="ri-calendar-line fs-4 me-3"></i>
                  <h4 className="mb-0 fw-bold">Filter Payrolls by Month</h4>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="align-items-end">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="fw-semibold text-dark mb-2">
                        <i className="ri-calendar-2-line me-2 text-primary"></i>
                        Select Month & Year
                      </Form.Label>
                      <Form.Control
                        type="month"
                        className="form-control-lg shadow-sm border-2"
                        value={`${startDate.getFullYear()}-${String(
                          startDate.getMonth() + 1
                        ).padStart(2, "0")}`}
                        onChange={(e) => handleDateChange(new Date(e.target.value))}
                        style={{ borderRadius: '12px' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <div className="text-center p-3 bg-light rounded-3">
                      <small className="text-muted d-block">Selected Period</small>
                      <strong className="text-primary">{startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Payrolls Section */}
        <Row className="g-3 g-lg-4 mb-5">
          {loading ? (
            <Col xs={12}>
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-muted">Loading your payrolls...</h5>
                <p className="text-muted">Please wait while we fetch your data</p>
              </div>
            </Col>
          ) : filteredPayrolls.length > 0 ? (
            filteredPayrolls.map((payroll, index) => (
              <Col xs={12} sm={6} lg={6} xl={4} key={payroll.id}>
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden payroll-card" 
                      style={{ 
                        transition: 'all 0.3s ease',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                        minHeight: '280px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px)';
                        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                      }}>
                  
                  {/* Card Header */}
                  <div className="position-relative">
                    <div className="bg-gradient-primary text-white p-3 text-center" 
                         style={{ background: `linear-gradient(135deg, ${index % 2 === 0 ? '#667eea 0%, #764ba2 100%' : '#f093fb 0%, #f5576c 100%'})` }}>
                      <div className="d-flex align-items-center justify-content-center mb-1">
                        <i className="ri-user-line fs-4 me-2"></i>
                        <h5 className="mb-0 fw-bold">{payroll.username}</h5>
                      </div>
                      <small className="opacity-75">Payroll Information</small>
                    </div>
                    <div className="position-absolute top-0 end-0 m-2">
                      <span className="badge bg-white text-primary px-2 py-1 rounded-pill fw-semibold">
                        #{index + 1}
                      </span>
                    </div>
                  </div>

                  <Card.Body className="p-3">
                    <Row className="g-2">
                      <Col xs={6}>
                        <div className="text-center p-2 bg-light rounded-3 d-flex flex-column justify-content-center" style={{ minHeight: '80px' }}>
                          <i className="ri-mail-line fs-5 text-info mb-1"></i>
                          <h6 className="fw-semibold text-dark mb-1 small">Email</h6>
                          <p className="mb-0 small text-muted text-truncate" title={payroll.email}>{payroll.email}</p>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-center p-2 bg-light rounded-3 d-flex flex-column justify-content-center" style={{ minHeight: '80px' }}>
                          <i className="ri-money-dollar-circle-line fs-5 text-success mb-1"></i>
                          <h6 className="fw-semibold text-dark mb-1 small">Biweekly Salary</h6>
                          <p className="mb-0 fw-bold text-success fs-6">${payroll.biweeklySalary}</p>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-center p-2 bg-light rounded-3 d-flex flex-column justify-content-center" style={{ minHeight: '80px' }}>
                          <i className="ri-time-line fs-5 text-warning mb-1"></i>
                          <h6 className="fw-semibold text-dark mb-1 small">Biweekly Hours</h6>
                          <p className="mb-0 fw-bold text-warning fs-6">{payroll.biweeklyHours}h</p>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-center p-2 bg-light rounded-3 d-flex flex-column justify-content-center" style={{ minHeight: '80px' }}>
                          <i className="ri-calendar-line fs-5 text-primary mb-1"></i>
                          <h6 className="fw-semibold text-dark mb-1 small">Date</h6>
                          <p className="mb-0 small text-muted">{payroll.date}</p>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className="text-center p-2 bg-primary bg-opacity-10 rounded-3" style={{ minHeight: '60px' }}>
                          <i className="ri-calendar-check-line fs-5 text-primary mb-1"></i>
                          <h6 className="fw-semibold text-dark mb-1 small">Created At</h6>
                          <p className="mb-0 small text-muted">
                            {new Date(payroll.CreatedAt).toLocaleString()}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ri-file-list-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="text-muted mb-3">No Payrolls Found</h4>
                <p className="text-muted mb-4">No payroll records found for the selected month.</p>
                <Button variant="outline-primary" size="lg" className="rounded-pill px-4">
                  <i className="ri-refresh-line me-2"></i>
                  Refresh Data
                </Button>
              </div>
            </Col>
          )}
        </Row>

        {/* Enhanced Document Upload Section */}
        <Row className="g-3 g-lg-4 mb-5">
          <Col xs={12}>
            <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
              <Card.Header className="bg-gradient-success text-white text-center py-4" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center justify-content-center">
                  <i className="ri-upload-cloud-2-line fs-4 me-3"></i>
                  <h4 className="mb-0 fw-bold">Upload Documents</h4>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold text-dark mb-3">
                        <i className="ri-file-add-line me-2 text-success"></i>
                        Select Documents to Upload
                      </Form.Label>
                      <Form.Control
                        type="file"
                        multiple
                        className="form-control-lg shadow-sm border-2"
                        onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                        style={{ borderRadius: '12px' }}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                      <Form.Text className="text-muted">
                        Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB each)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <Button
                        variant="success"
                        size="lg"
                        onClick={handleUpload}
                        disabled={uploading || selectedFiles.length === 0}
                        className="rounded-pill px-4 py-3 shadow-sm"
                        style={{ minWidth: '150px' }}
                      >
                        {uploading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="ri-upload-line me-2"></i>
                            Upload Files
                          </>
                        )}
                      </Button>
                      {selectedFiles.length > 0 && (
                        <div className="mt-3">
                          <small className="text-success fw-semibold">
                            {selectedFiles.length} file(s) selected
                          </small>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Uploaded Documents List */}
        <Row className="g-3 g-lg-4">
          <Col xs={12}>
            <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
              <Card.Header className="bg-gradient-info text-white text-center py-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center justify-content-center">
                  <i className="ri-folder-open-line fs-4 me-3"></i>
                  <h4 className="mb-0 fw-bold">Uploaded Documents</h4>
                  {documents.length > 0 && (
                    <span className="badge bg-white text-info ms-3 px-3 py-2 rounded-pill">
                      {documents.length} file(s)
                    </span>
                  )}
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {documents.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="ri-folder-open-line text-muted" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h5 className="text-muted mb-3">No Documents Uploaded</h5>
                    <p className="text-muted">You haven't uploaded any documents yet.</p>
                  </div>
                ) : (
                  <div className="row g-3">
                    {documents.map((doc, index) => (
                      <Col xs={12} sm={6} lg={4} key={doc.id}>
                        <div className="card border-0 shadow-sm rounded-3 h-100 document-card"
                             style={{ transition: 'all 0.3s ease' }}
                             onMouseEnter={(e) => {
                               e.currentTarget.style.transform = 'translateY(-4px)';
                               e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                             }}
                             onMouseLeave={(e) => {
                               e.currentTarget.style.transform = 'translateY(0)';
                               e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                             }}>
                          <div className="card-body p-4">
                            <div className="d-flex align-items-start mb-3">
                              <div className="flex-shrink-0 me-3">
                                <div className="bg-primary bg-opacity-10 rounded-3 p-3">
                                  <i className="ri-file-text-line fs-4 text-primary"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="fw-semibold text-dark mb-1 text-truncate" title={doc.fileName}>
                                  {doc.fileName}
                                </h6>
                                <small className="text-muted">
                                  <i className="ri-time-line me-1"></i>
                                  {new Date(doc.uploadedAt).toLocaleDateString()}
                                </small>
                              </div>
                            </div>
                            <div className="d-grid">
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                href={doc.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="rounded-pill"
                              >
                                <i className="ri-external-link-line me-2"></i>
                                View Document
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <Footer />
    </>
  );
}
